import { useEffect, useState } from "react";
import { client } from "../../lib/config";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { CheckCircle2, XCircle, ExternalLink } from "lucide-react";

/**
 * Field metadata. Each entry may include:
 *   group      — visual section it belongs to
 *   help       — short helper text under the input
 *   link       — { url, label } shown next to help so the owner can go get the value
 *   placeholder — hint inside the input
 *   type       — "text" (default) | "textarea" | "url"
 *   write_only — true for secrets; server returns a masked read-only preview
 */
const GROUPS = [
  {
    id: "brand",
    title: "Brand & canonical URL",
    intro: "The canonical production URL of the site. Every canonical tag, sitemap URL, robots.txt Sitemap: line, and llms.txt link is built from this value.",
    fields: [
      {
        name: "site_base_url",
        label: "Site base URL",
        placeholder: "https://www.westbridgeinnandsuites.com",
        help: "Must include the scheme and the www subdomain that matches your Google Search Console property (e.g. https://www.westbridgeinnandsuites.com). No trailing slash.",
      },
      { name: "hero_headline", label: "Homepage hero headline", type: "textarea" },
      { name: "hero_subhead",  label: "Homepage hero subhead",  type: "textarea" },
    ],
  },
  {
    id: "analytics",
    title: "Analytics & search-engine verification",
    intro: "Add these once and they inject site-wide via the SEO head. Leave any field empty to disable that integration.",
    fields: [
      {
        name: "ga4_id",
        label: "GA4 Measurement ID",
        placeholder: "G-XXXXXXXXXX",
        help: "Format: G-XXXXXXXXXX. Grab it in analytics.google.com → Admin → Data Streams → your web stream → Measurement ID.",
        link: { url: "https://analytics.google.com/", label: "Open Google Analytics" },
      },
      {
        name: "google_site_verification",
        label: "Google Search Console verification token",
        placeholder: "e.g. Abc123DefGhi...",
        help: "In Search Console, pick the 'HTML tag' verification method and paste ONLY the value of the content=\"…\" attribute here (no quotes, no <meta> tag).",
        link: { url: "https://search.google.com/search-console", label: "Open Search Console" },
      },
      {
        name: "bing_site_verification",
        label: "Bing Webmaster verification token",
        placeholder: "e.g. 1234ABCD5678EF...",
        help: "In Bing Webmaster Tools, choose 'Meta tag' verification and paste the content=\"…\" value here.",
        link: { url: "https://www.bing.com/webmasters/", label: "Open Bing Webmaster" },
      },
    ],
  },
  {
    id: "gbp",
    title: "Google Business Profile",
    intro: "Used for the QR review card and (once the API key is set) live Google reviews on the homepage.",
    fields: [
      {
        name: "gbp_review_link",
        label: "GBP review link",
        placeholder: "https://g.page/r/…/review",
        help: "The 'Ask for reviews' short URL from your Business Profile dashboard. Test in an incognito tab — it should open the review dialog directly.",
        link: { url: "https://business.google.com/", label: "Open Business Profile" },
      },
      {
        name: "google_place_id",
        label: "Google Place ID",
        placeholder: "ChIJ…",
        help: "Starts with 'ChIJ'. Grab it from the Place ID Finder — this is what the live-reviews integration calls.",
        link: { url: "https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder", label: "Open Place ID Finder" },
      },
      {
        name: "google_places_api_key",
        label: "Google Places API key (secret)",
        placeholder: "Paste new key to rotate; leave blank to keep current",
        help: "Restrict this key to HTTP referrer https://www.westbridgeinnandsuites.com/* and to the Places API (New) only. Never returned in cleartext; a masked preview shows here once set.",
        link: { url: "https://console.cloud.google.com/apis/credentials", label: "Open Google Cloud credentials" },
        write_only: true,
      },
      {
        name: "gbp_place_id",
        label: "GBP Place ID (legacy field)",
        help: "Legacy field kept for backwards compatibility. New reviews integration uses Google Place ID above.",
      },
    ],
  },
];

const CITATION_PLATFORMS = [
  { key: "yelp",        label: "Yelp",         placeholder: "https://www.yelp.com/biz/…" },
  { key: "tripadvisor", label: "TripAdvisor",  placeholder: "https://www.tripadvisor.com/Hotel_Review-…" },
  { key: "facebook",    label: "Facebook",     placeholder: "https://www.facebook.com/westbridgeinn" },
  { key: "instagram",   label: "Instagram",    placeholder: "https://www.instagram.com/westbridgeinn" },
  { key: "apple",       label: "Apple Maps",   placeholder: "https://maps.apple.com/place?…" },
  { key: "x",           label: "X / Twitter",  placeholder: "https://x.com/westbridgeinn" },
  { key: "linkedin",    label: "LinkedIn",     placeholder: "https://www.linkedin.com/company/…" },
  { key: "youtube",     label: "YouTube",      placeholder: "https://www.youtube.com/@westbridgeinn" },
];

function FieldRow({ f, state, setState, errors }) {
  const currentVal = f.write_only ? state[f.name] || "" : (state[f.name] ?? "");
  const placeholder = f.write_only && state.google_places_api_key_masked
    ? state.google_places_api_key_masked
    : (f.placeholder || "");
  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <Label htmlFor={f.name} className="font-bold">{f.label}</Label>
        {f.link && (
          <a
            href={f.link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-bold text-[#B94A15] hover:text-[#8F390F] inline-flex items-center gap-1"
            data-testid={`settings-link-${f.name}`}
          >
            {f.link.label} <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        )}
      </div>
      {f.type === "textarea" ? (
        <textarea
          id={f.name}
          value={currentVal}
          onChange={(e) => setState({ ...state, [f.name]: e.target.value })}
          data-testid={`settings-field-${f.name}`}
          rows={2}
          className="mt-1 w-full px-3 py-2 border border-[#E5DFD3] rounded-sm bg-white text-sm"
          placeholder={placeholder}
        />
      ) : (
        <Input
          id={f.name}
          value={currentVal}
          onChange={(e) => setState({ ...state, [f.name]: e.target.value })}
          data-testid={`settings-field-${f.name}`}
          className="mt-1"
          placeholder={placeholder}
          type={f.write_only ? "password" : "text"}
          autoComplete={f.write_only ? "off" : undefined}
        />
      )}
      {f.help && <div className="text-xs text-[#4A4641] mt-1 leading-relaxed">{f.help}</div>}
      {errors[f.name] && <div className="text-xs text-[#B93D12] mt-1">{errors[f.name]}</div>}
    </div>
  );
}

export default function AdminSettingsPage() {
  const [state, setState] = useState({});
  const [errors, setErrors] = useState({});
  const [citationRows, setCitationRows] = useState({}); // { yelp: "url", ... }
  const [extraCitations, setExtraCitations] = useState(""); // multiline extras
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const runReviewsTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await client.post("/admin/reviews/test");
      setTestResult(r.data);
    } catch (err) {
      setTestResult({ ok: false, reason: err?.response?.data?.detail || "Test failed" });
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    client.get("/settings/all").then((r) => {
      const d = r.data;
      setState(d);
      const rows = {};
      const remaining = [];
      const known = new Map(CITATION_PLATFORMS.map((p) => [p.key, p]));
      (d.citation_urls || []).forEach((u) => {
        const low = u.toLowerCase();
        let assigned = false;
        for (const [key] of known) {
          if (rows[key]) continue;
          const kkey = key === "x" ? /(^https?:\/\/)?(x\.com|twitter\.com)/ : new RegExp(`${key}\\.`);
          if (kkey.test(low)) { rows[key] = u; assigned = true; break; }
        }
        if (!assigned) remaining.push(u);
      });
      setCitationRows(rows);
      setExtraCitations(remaining.join("\n"));
      setLoaded(true);
    }).catch(() => toast.error("Failed to load settings"));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    const payload = {};
    GROUPS.forEach((g) => g.fields.forEach((f) => {
      if (f.write_only) {
        if (state[f.name]) payload[f.name] = state[f.name];
      } else if (state[f.name] !== undefined) {
        payload[f.name] = state[f.name];
      }
    }));
    const citation_urls = [
      ...Object.values(citationRows).filter((v) => v && v.trim()),
      ...extraCitations.split(/\n+/).map((s) => s.trim()).filter(Boolean),
    ];
    payload.citation_urls = citation_urls;
    try {
      const r = await client.put("/settings", payload);
      toast.success("Saved");
      setState({ ...r.data, google_places_api_key: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (Array.isArray(d)) {
        const map = {};
        d.forEach((it) => { const loc = it.loc?.slice(-1)[0]; if (loc) map[loc] = it.msg; });
        setErrors(map);
        toast.error("Please fix the highlighted fields");
      } else {
        toast.error(typeof d === "string" ? d : "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="text-[#4A4641]">Loading…</div>;

  return (
    <>
      <div className="mb-6">
        <div className="wb-eyebrow">Settings</div>
        <h1 className="wb-h2 mt-2">Site settings & integrations</h1>
        <p className="wb-body mt-3 max-w-2xl">
          Everything here is safe for the owner to update. Each field explains where to get its value.
          Secrets are masked after save.
        </p>
      </div>
      <form onSubmit={save} className="space-y-6 max-w-3xl" data-testid="admin-settings-form">
        {GROUPS.map((g) => (
          <section key={g.id} className="wb-card bg-white p-6 md:p-8" data-testid={`settings-group-${g.id}`}>
            <h2
              className="text-xl font-black text-[#1A1A1A]"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              {g.title}
            </h2>
            {g.intro && <p className="wb-body text-sm mt-2 text-[#4A4641]">{g.intro}</p>}
            <div className="mt-6 space-y-5">
              {g.fields.map((f) => (
                <FieldRow key={f.name} f={f} state={state} setState={setState} errors={errors} />
              ))}
              {g.id === "gbp" && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={runReviewsTest}
                    disabled={testing}
                    className="wb-btn-outline"
                    data-testid="settings-test-reviews"
                  >
                    {testing ? "Testing…" : "Test Google Reviews connection"}
                  </button>
                  {testResult && (
                    <div
                      className={`mt-3 text-sm flex items-center gap-2 ${testResult.ok ? "text-green-700" : "text-[#B93D12]"}`}
                      data-testid="settings-test-result"
                    >
                      {testResult.ok
                        ? <><CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Live: {Number(testResult.rating).toFixed(1)}★ · {testResult.review_count} reviews · {testResult.reviews_returned} shown</>
                        : <><XCircle className="h-4 w-4" aria-hidden="true" /> {typeof testResult.reason === "string" ? testResult.reason : JSON.stringify(testResult.reason).slice(0, 200)}</>}
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        ))}

        {/* Citation / listing URLs */}
        <section className="wb-card bg-white p-6 md:p-8" data-testid="settings-group-citations">
          <h2
            className="text-xl font-black text-[#1A1A1A]"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            Citation & listing URLs
          </h2>
          <p className="wb-body text-sm mt-2 text-[#4A4641]">
            Paste the public URL of each listing so the site can link out and so Local SEO citations stay consistent. Leave blank if you're not on that platform yet.
          </p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            {CITATION_PLATFORMS.map((p) => (
              <div key={p.key}>
                <Label htmlFor={`citation-${p.key}`} className="font-bold">{p.label}</Label>
                <Input
                  id={`citation-${p.key}`}
                  value={citationRows[p.key] || ""}
                  onChange={(e) => setCitationRows({ ...citationRows, [p.key]: e.target.value })}
                  data-testid={`settings-citation-${p.key}`}
                  className="mt-1"
                  placeholder={p.placeholder}
                  type="url"
                />
              </div>
            ))}
          </div>
          <div className="mt-5">
            <Label htmlFor="extra-citations">Other citation URLs (one per line)</Label>
            <textarea
              id="extra-citations"
              value={extraCitations}
              onChange={(e) => setExtraCitations(e.target.value)}
              data-testid="settings-field-citation_urls"
              rows={4}
              className="mt-1 w-full px-3 py-2 border border-[#E5DFD3] rounded-sm bg-white text-sm font-mono"
              placeholder="https://…"
            />
            <div className="text-xs text-[#4A4641] mt-1">
              Anything not in the platforms above — e.g. Booking.com, Expedia, chamber of commerce, local directories.
            </div>
            {errors.citation_urls && <div className="text-xs text-[#B93D12] mt-1">{errors.citation_urls}</div>}
          </div>
        </section>

        <div className="pt-2 flex flex-wrap items-center gap-3">
          <button type="submit" disabled={saving} className="wb-btn-primary" data-testid="settings-save">
            {saving ? "Saving…" : "Save changes"}
          </button>
          {state.google_places_api_key_masked && (
            <span className="text-xs text-[#4A4641]" data-testid="settings-key-masked">
              Places API key on file: {state.google_places_api_key_masked}
            </span>
          )}
        </div>
      </form>
    </>
  );
}
